import { tryDecodeUri } from "../../../../utilities/EncodeDecode";

// Built-in SharePoint document libraries that must never be surfaced as user-selectable
// libraries (e.g. the Preservation Hold Library used for retention/eDiscovery holds).
//
// Matching is done against the site-relative URL segment, NEVER the display name: display
// names are localized per tenant/site language (the Preservation Hold Library shows as
// "Opslagbibliotheek" in a Dutch tenant), while the URL segment is set at creation time and
// stays language-invariant. Keys are stored lower-cased; comparison is case-insensitive.
const systemLibraryPaths = new Set<string>([
  "preservationholdlibrary",   // Preservation Hold Library (retention / eDiscovery holds)
  "sitecollectiondocuments",   // Site Collection Documents (publishing infrastructure)
  "sitecollectionimages",      // Site Collection Images (publishing infrastructure)
  "style library",             // Style Library (publishing) - note the space in the segment
  "formservertemplates",       // Form Templates (InfoPath Forms Services)
  "siteassets",                // Site Assets (modern site default; also holds the group OneNote)
  "sitepages",                 // Site Pages (modern pages library)
  "publishingimages",          // Publishing Images (publishing sites)
  "dropofflibrary",            // Drop Off Library (Content Organizer) - NOT a user "/Dropoff"
  "appcatalog",                // Apps for SharePoint (app-catalog sites)
  "clientsideassets",          // Client Side Assets (SPFx asset library on app-catalog sites)
  "iwconvertedforms",          // Converted Forms (InfoPath form conversion - form-enabled sites)
  "teams wiki data"            // Teams channel wiki library - note the spaces in the segment
  // NOTE: bare "documents" and "pages" are deliberately NOT excluded.
  //  - "documents" is the PRIMARY user library on OneDrive personal sites
  //    (.../personal/<user>/Documents) - excluding it would hide the user's own files.
  //  - "pages" is only a system library on classic publishing sites (which Docubird's
  //    site filters already exclude); on other sites a user could legitimately own a
  //    "/Pages" library. The invariant system page library "sitepages" is still excluded.
]);

// System libraries whose URL segment carries a generated/instance-specific suffix and so must
// be matched by prefix rather than by exact segment. Stored lower-cased.
const systemLibraryPathPrefixes: string[] = [
  "_catalogs/",                     // master page / web part / list template / solution galleries
  "persistedmanagednavigationlist"  // managed navigation storage list
];

// Returns the decoded, slash-trimmed site-relative path of a library (e.g.
// "PreservationHoldLibrary" or "_catalogs/masterpage"), or undefined when the library url does
// not sit under the supplied site url. Spaces arrive either literally or as %20 depending on the
// API, so the path is decoded to make matching robust against either form.
export const getSiteRelativeLibraryPath = (webUrl?: string, siteUrl?: string): string | undefined => {
  if (!webUrl || !siteUrl) {
    return undefined;
  }

  const normalizedSiteUrl = siteUrl.replace(/\/+$/, "");

  // Compare only the leading site-url-length slice (case-insensitively) instead of lower-casing
  // the whole web url: it is cheaper and keeps the boundary index below aligned with the exact
  // characters that were compared.
  if (webUrl.slice(0, normalizedSiteUrl.length).toLowerCase() !== normalizedSiteUrl.toLowerCase()) {
    return undefined;
  }

  // Guard against prefix collisions (e.g. ".../sites/Beeld" vs ".../sites/Beeldbank"): the
  // site url must be the whole web url or be followed by a path separator.
  const boundaryChar = webUrl.charAt(normalizedSiteUrl.length);
  if (boundaryChar !== "" && boundaryChar !== "/") {
    return undefined;
  }

  const remainder = webUrl.substring(normalizedSiteUrl.length).replace(/^\/+|\/+$/g, "");

  // decodeURI only rewrites %XX escapes, so a remainder with no "%" is returned unchanged - skip
  // the decode (the costliest step here) on that common path.
  return remainder.includes("%") ? tryDecodeUri(remainder) : remainder;
};

// True when the library identified by webUrl is a built-in SharePoint system library that should
// be hidden from the add-in and Content Manager.
export const isSystemLibrary = (webUrl?: string, siteUrl?: string): boolean => {
  const relativePath = getSiteRelativeLibraryPath(webUrl, siteUrl);
  if (!relativePath) {
    return false;
  }

  const key = relativePath.toLowerCase();
  if (systemLibraryPaths.has(key)) {
    return true;
  }

  return systemLibraryPathPrefixes.some(prefix => key.startsWith(prefix));
};
